import type { Components } from 'react-markdown';
import Markdown from 'react-markdown';
import remarkBreaks from 'remark-breaks';
import remarkGfm from 'remark-gfm';
import { Loader2 } from 'lucide-react';
import { useEffect, useState } from 'react';
import { AuthenticatedImg } from '../../utils/authenticatedBlobImage';
import { AiGeneratedFigureFrame } from './AiGeneratedFigureFrame';
import { figureDiagramAriaLabel, parseFigureDiagramSpec } from './figureDiagramSpec';
import { InlineSvgFigure } from './InlineSvgFigure';
import { LessonFigureDiagram, type LessonFigureRegenerateContext } from './LessonFigureDiagram';
import { normalizeFigureKey } from './loadingStateKeys';
import { RasterFigureReviewCard } from './RasterFigureReviewCard';
import {
  findRasterFigureByKey,
  isTypedFigureSpec,
  resolveRasterFigureImageSrc,
  splitSupportingMaterialContent,
  type RasterSlotKind,
} from './supportingMaterialContentSegments';
import type { InstructionStepDto, StepRasterFigureDto } from './types';
import type { MediaReviewSlotKind } from './mediaReviewUtils';
import {
  isAiGeneratedRasterFigure,
  isRasterFigureAwaitingImage,
  isRasterFigureFailed,
  isRasterFigureGenerating,
  isRasterFigureReady,
  RASTER_GENERATING_MESSAGE,
  resolveStepIdForMediaCommand,
  vectorDiagramDomId,
} from './mediaReviewUtils';

const supportingMaterialMarkdownComponents: Components = {
  p: ({ children }) => (
    <p className="mb-2 last:mb-0 leading-relaxed text-sm text-gray-700">{children}</p>
  ),
  ul: ({ children }) => (
    <ul className="my-2 list-disc space-y-1 pl-4 text-sm text-gray-700">{children}</ul>
  ),
  ol: ({ children }) => (
    <ol className="my-2 list-decimal space-y-1 pl-4 text-sm text-gray-700">{children}</ol>
  ),
  li: ({ children }) => <li className="leading-relaxed">{children}</li>,
  strong: ({ children }) => (
    <strong className="font-semibold text-gray-900">{children}</strong>
  ),
  em: ({ children }) => <em className="italic">{children}</em>,
  h1: ({ children }) => (
    <p className="mb-2 text-base font-semibold text-gray-900">{children}</p>
  ),
  h2: ({ children }) => (
    <p className="mb-2 text-sm font-semibold text-gray-900">{children}</p>
  ),
  h3: ({ children }) => (
    <p className="mb-1.5 text-sm font-semibold text-gray-900">{children}</p>
  ),
  hr: () => <hr className="my-3 border-gray-200" />,
  table: ({ children }) => (
    <div className="my-3 overflow-x-auto">
      <table className="min-w-full border-collapse border border-gray-300 text-sm text-gray-700">
        {children}
      </table>
    </div>
  ),
  thead: ({ children }) => <thead className="bg-gray-50">{children}</thead>,
  tbody: ({ children }) => <tbody>{children}</tbody>,
  tr: ({ children }) => <tr className="border-b border-gray-200">{children}</tr>,
  th: ({ children }) => (
    <th className="border border-gray-300 px-3 py-2 text-left font-semibold text-gray-900">
      {children}
    </th>
  ),
  td: ({ children }) => (
    <td className="border border-gray-300 px-3 py-2 align-top">{children}</td>
  ),
  blockquote: ({ children }) => (
    <blockquote className="my-2 border-l-2 border-gray-300 pl-3 text-sm text-gray-700">
      {children}
    </blockquote>
  ),
};

function SupportingMaterialMarkdownSegment({ content }: { content: string }) {
  if (!content?.trim()) return null;

  return (
    <Markdown
      remarkPlugins={[remarkGfm, remarkBreaks]}
      components={supportingMaterialMarkdownComponents}
    >
      {content}
    </Markdown>
  );
}

export type RasterFigureReviewContext = {
  videoReviewMode?: boolean;
  instructionSteps?: InstructionStepDto[];
  isRasterApproved?: (stepId: string | undefined, figureKey: string | undefined) => boolean;
  isRasterApproving?: (stepId: string | undefined, figureKey: string | undefined) => boolean;
  onApproveRaster?: (stepId: string | undefined, figureKey: string | undefined) => void;
  onRegenerateRaster?: (
    stepId: string | undefined,
    figureKey: string | undefined,
    revisionNotes?: string
  ) => void;
  isRasterRegenerating?: (stepId: string | undefined, figureKey: string | undefined) => boolean;
  onAcceptTextOnly?: (stepId: string | undefined, figureKey: string | undefined) => void;
  isAcceptingTextOnly?: (stepId: string | undefined, figureKey: string | undefined) => boolean;
  hostMaterialKind?: 'worksheet' | 'reading';
  onOpenRegenerateHostMaterial?: () => void;
  onOpenRegenerateLesson?: () => void;
  onOpenRegenerateUnit?: () => void;
  onUploadRaster?: (
    stepId: string | undefined,
    figureKey: string | undefined,
    blobId: string,
    title?: string
  ) => void;
  isRasterUploading?: (stepId: string | undefined, figureKey: string | undefined) => boolean;
  onCheckRasterStatus?: (stepId: string | undefined, figureKey: string | undefined) => void;
  isRasterStatusChecking?: (stepId: string | undefined, figureKey: string | undefined) => boolean;
  isRasterStillGeneratingAfterCheck?: (
    stepId: string | undefined,
    figureKey: string | undefined
  ) => boolean;
  buildMediaSlotDomId?: (
    kind: MediaReviewSlotKind,
    materialId: string | undefined,
    figureKey?: string
  ) => string | undefined;
};

type SupportingMaterialMarkdownProps = {
  content: string;
  className?: string;
  figureRegenerateContext?: LessonFigureRegenerateContext;
  /** Print HTML uses plain <img>; the live lesson view fetches blobs with the session token. */
  rasterImages?: 'authenticated' | 'plain';
  rasterReview?: RasterFigureReviewContext;
  /** Static print HTML omits interactive figure controls (copy, enlarge, regenerate). */
  variant?: 'live' | 'print';
};

function PrintFigureDiagram({
  figureKey,
  svg,
  spec,
}: {
  figureKey: string;
  svg: string;
  spec?: string;
}) {
  const diagramSpec = parseFigureDiagramSpec(spec);
  const ariaLabel = figureDiagramAriaLabel(diagramSpec, normalizeFigureKey(figureKey));

  return (
    <figure className="my-3">
      <AiGeneratedFigureFrame className="block w-full">
        <InlineSvgFigure svg={svg} ariaLabel={ariaLabel} />
      </AiGeneratedFigureFrame>
    </figure>
  );
}

function RasterFigureImage({
  src,
  alt,
  figureKey,
  mode,
  showAiWatermark,
}: {
  src: string;
  alt: string;
  figureKey: string;
  mode: 'authenticated' | 'plain';
  showAiWatermark?: boolean;
}) {
  const image =
    mode === 'plain' ? (
      <img
        src={src}
        alt={alt}
        data-figure-key={figureKey}
        data-ai-generated={showAiWatermark ? 'true' : undefined}
        style={{ maxWidth: '100%', height: 'auto', display: 'block', margin: '0.75em auto' }}
      />
    ) : (
      <AuthenticatedImg
        src={src}
        alt={alt}
        className="block h-auto max-w-full"
      />
    );

  return (
    <AiGeneratedFigureFrame show={Boolean(showAiWatermark)} className="my-3 block w-full">
      {image}
    </AiGeneratedFigureFrame>
  );
}

function DeliveryRasterPlaceholder({
  figure,
  kind,
}: {
  figure?: StepRasterFigureDto;
  kind: RasterSlotKind;
}) {
  const [now, setNow] = useState(() => Date.now());

  useEffect(() => {
    if (!figure) return;
    if (!isRasterFigureGenerating(figure, Date.now())) return;
    const id = window.setInterval(() => setNow(Date.now()), 15_000);
    return () => window.clearInterval(id);
  }, [figure?.e251_GenerationState, figure?.e251_GenerationStartedAt]);

  if (figure && isRasterFigureAwaitingImage(figure)) {
    return (
      <div className="my-3 rounded-md border border-dashed border-gray-300 bg-gray-50 px-4 py-6 text-center text-sm text-gray-600">
        Upload a photograph for this topic image, or regenerate to create one with AI.
      </div>
    );
  }

  if (
    (figure && isRasterFigureGenerating(figure, now)) ||
    kind === 'generating' ||
    kind === 'pending'
  ) {
    return (
      <div
        className="my-3 flex flex-col items-center justify-center gap-3 rounded-md border border-dashed border-gray-300 bg-gray-50 px-4 py-10 text-center text-sm text-gray-600"
        role="status"
        aria-live="polite"
      >
        <Loader2 className="h-8 w-8 animate-spin text-[#1a5172]" aria-hidden />
        <p>{RASTER_GENERATING_MESSAGE}</p>
      </div>
    );
  }

  if (
    kind === 'failed' ||
    (figure && isRasterFigureFailed(figure, now) && (figure.e251_RasterOptions?.length ?? 0) === 0)
  ) {
    return (
      <div className="my-3 rounded-md border border-[#D4616B]/30 bg-white p-3 text-sm text-[#BE5059]">
        {figure?.e251_GenerationError || 'Image generation failed. Please regenerate.'}
      </div>
    );
  }

  return null;
}

function InlineRasterSlot({
  figureKey,
  kind,
  contentSrc,
  contentAlt,
  contentAiGenerated,
  rasterImages,
  rasterReview,
}: {
  figureKey: string;
  kind: RasterSlotKind;
  contentSrc?: string;
  contentAlt?: string;
  contentAiGenerated?: boolean;
  rasterImages: 'authenticated' | 'plain';
  rasterReview?: RasterFigureReviewContext;
}) {
  const located = findRasterFigureByKey(rasterReview?.instructionSteps, figureKey);
  const figure = located?.figure;
  const stepId = located?.stepId;
  const reviewMode = Boolean(rasterReview?.videoReviewMode);

  if (reviewMode && figure && stepId) {
    return (
      <div className="my-3">
        <RasterFigureReviewCard
          stepId={stepId}
          figure={figure}
          instructionSteps={rasterReview?.instructionSteps}
          videoReviewMode={true}
          isRasterApproved={rasterReview?.isRasterApproved}
          isRasterApproving={rasterReview?.isRasterApproving}
          onApproveRaster={rasterReview?.onApproveRaster}
          onRegenerateRaster={rasterReview?.onRegenerateRaster}
          isRasterRegenerating={rasterReview?.isRasterRegenerating}
          onAcceptTextOnly={rasterReview?.onAcceptTextOnly}
          isAcceptingTextOnly={rasterReview?.isAcceptingTextOnly}
          hostMaterialKind={rasterReview?.hostMaterialKind}
          onOpenRegenerateHostMaterial={rasterReview?.onOpenRegenerateHostMaterial}
          onOpenRegenerateLesson={rasterReview?.onOpenRegenerateLesson}
          onOpenRegenerateUnit={rasterReview?.onOpenRegenerateUnit}
          onUploadRaster={rasterReview?.onUploadRaster}
          isRasterUploading={rasterReview?.isRasterUploading}
          onCheckRasterStatus={rasterReview?.onCheckRasterStatus}
          isRasterStatusChecking={rasterReview?.isRasterStatusChecking}
          isRasterStillGeneratingAfterCheck={rasterReview?.isRasterStillGeneratingAfterCheck}
          buildMediaSlotDomId={rasterReview?.buildMediaSlotDomId}
          inline
        />
      </div>
    );
  }

  const metaSrc = figure ? resolveRasterFigureImageSrc(figure) : '';
  const src = contentSrc?.trim() || metaSrc;
  if (src) {
    return (
      <RasterFigureImage
        src={src}
        alt={contentAlt || figure?.e251_RasterTitle || figureKey}
        figureKey={figureKey}
        mode={rasterImages}
        showAiWatermark={
          figure ? isAiGeneratedRasterFigure(figure) : Boolean(contentAiGenerated)
        }
      />
    );
  }

  if (figure && isRasterFigureReady(figure)) {
    return null;
  }

  return <DeliveryRasterPlaceholder figure={figure} kind={kind} />;
}

export function SupportingMaterialMarkdown({
  content,
  className,
  figureRegenerateContext,
  rasterImages = 'authenticated',
  rasterReview,
  variant = 'live',
}: SupportingMaterialMarkdownProps) {
  if (!content?.trim()) return null;

  const segments = splitSupportingMaterialContent(content);
  if (segments.length === 0) return null;

  return (
    <div className={className ?? ''}>
      {segments.map((segment, index) => {
        if (segment.type === 'figure') {
          if (variant === 'print') {
            return (
              <PrintFigureDiagram
                key={`figure-${segment.key}-${index}`}
                figureKey={segment.key}
                svg={segment.svg}
                spec={segment.spec}
              />
            );
          }

          return (
            <LessonFigureDiagram
              key={`figure-${segment.key}-${index}`}
              figureKey={segment.key}
              svg={segment.svg}
              spec={segment.spec}
              failed={segment.failed}
              canRegenerate={isTypedFigureSpec(segment.spec)}
              regenerateContext={figureRegenerateContext}
              domId={
                figureRegenerateContext
                  ? vectorDiagramDomId(
                      figureRegenerateContext.unitId,
                      figureRegenerateContext.lessonId,
                      resolveStepIdForMediaCommand(figureRegenerateContext.materialId)
                        ?? figureRegenerateContext.materialId,
                      segment.key
                    )
                  : undefined
              }
            />
          );
        }
        if (segment.type === 'raster') {
          return (
            <InlineRasterSlot
              key={`raster-${segment.key}-${index}`}
              figureKey={segment.key}
              kind="ready"
              contentSrc={segment.src}
              contentAlt={segment.alt}
              contentAiGenerated={segment.aiGenerated}
              rasterImages={rasterImages}
              rasterReview={rasterReview}
            />
          );
        }
        if (segment.type === 'raster_slot') {
          return (
            <InlineRasterSlot
              key={`raster-slot-${segment.key}-${index}`}
              figureKey={segment.key}
              kind={segment.kind}
              contentSrc={segment.src}
              contentAlt={segment.alt}
              rasterImages={rasterImages}
              rasterReview={rasterReview}
            />
          );
        }
        return <SupportingMaterialMarkdownSegment key={`md-${index}`} content={segment.content} />;
      })}
    </div>
  );
}

export { supportingMaterialMarkdownComponents };
